// src/pages/pmc/PmcFieldVisitPane.js

import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { db, storage } from "../../firebaseConfig";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import jsPDF from "jspdf";

const cx = (...xs) => xs.filter(Boolean).join(" ");

function clean(v = "") {
  return String(v || "").trim().toLowerCase();
}

function normalizeRow(row = {}) {
  return {
    ...row,
    state: String(
      row.state || row.stateName || row.projectState || row.pacsState || ""
    ).trim(),
    district: String(
      row.district ||
        row.districtName ||
        row.projectDistrict ||
        row.pacsDistrict ||
        ""
    ).trim(),
    taluka: String(row.taluka || row.block || row.pacsTaluka || "").trim(),
    village: String(row.village || row.location || row.pacsVillage || "").trim(),
  };
}

function isStrictNafedApply(row = {}) {
  return (
    row.nafedProject === true &&
    row.projectSource === "NAFED" &&
    row.applicationPortal === "NAFED_PACS_APPLY"
  );
}

// Applications are soft-deleted to protect linked MOU, tender and workflow
// records. Archived records must not appear in active field-visit operations.
function isActiveProject(row = {}) {
  const archiveState = String(
    row.archiveStatus || row.recordStatus || row.deletionStatus || ""
  ).toUpperCase();

  return (
    row.isDeleted !== true &&
    row.deleted !== true &&
    row.archived !== true &&
    row.isArchived !== true &&
    ![
      "DELETED",
      "ARCHIVED",
      "DELETED_FROM_PMC_ACTIVE_LIST",
      "REMOVED_FROM_ACTIVE_LIST",
    ].includes(archiveState)
  );
}

function applyNafedScopeFilter(list = [], nafedScope = {}) {
  const scopeState = clean(nafedScope?.state);

  const allowedDistricts = (
    nafedScope?.allowedDistricts ||
    nafedScope?.districts ||
    []
  ).map(clean);

  return list.filter((row) => {
    const stateOk = !scopeState || clean(row.state) === scopeState;
    const districtOk =
      !allowedDistricts.length || allowedDistricts.includes(clean(row.district));

    return (
      isStrictNafedApply(row) &&
      isActiveProject(row) &&
      stateOk &&
      districtOk
    );
  });
}

function formatDate(value) {
  if (!value) return "—";
  try {
    const d = value?.toDate ? value.toDate() : new Date(value);
    return dayjs(d).format("DD/MM/YYYY");
  } catch {
    return "—";
  }
}

function Badge({ type = "pending", children }) {
  return <span className={`fv-badge ${type}`}>{children}</span>;
}

function StatCard({ label, value }) {
  return (
    <div className="fv-stat">
      <label>{label}</label>
      <b>{value}</b>
    </div>
  );
}

function exportCsv(rows) {
  const headers = [
    "Sr",
    "Application ID",
    "PACS Name",
    "State",
    "District",
    "Taluka",
    "Village",
    "Capacity",
    "Visit Status",
    "Assigned To",
    "Visit Date",
    "Report",
  ];

  const data = rows.map((r, i) => [
    i + 1,
    r.applicationId || r.id || "",
    r.pacsName || "",
    r.state || "",
    r.district || "",
    r.taluka || "",
    r.village || "",
    r.capacityMt || "",
    r.siteVisitStatus || "NEW",
    r.siteVisitAssignedTo || "",
    r.siteVisitPlanDate || "",
    r.siteVisitReportText || "",
  ]);

  const csv = [headers, ...data]
    .map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "NAFED_Field_Visit_Report.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function exportPdf(rows) {
  const pdf = new jsPDF("l", "pt", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  let y = 42;

  pdf.setFillColor(6, 43, 85);
  pdf.rect(0, 0, pageWidth, 55, "F");

  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.text("NAFED Field Visit Report", 40, 34);

  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(8);
  y = 80;

  const headers = [
    "Sr",
    "Application",
    "PACS",
    "State",
    "District",
    "Taluka",
    "Capacity",
    "Visit",
    "Assigned",
  ];

  const widths = [28, 110, 170, 80, 90, 90, 60, 70, 130];

  const drawHeader = () => {
    let x = 28;
    pdf.setFont("helvetica", "bold");
    pdf.setFillColor(226, 232, 240);

    headers.forEach((h, i) => {
      pdf.rect(x, y, widths[i], 24, "FD");
      pdf.text(h, x + 4, y + 15);
      x += widths[i];
    });

    y += 24;
    pdf.setFont("helvetica", "normal");
  };

  drawHeader();

  rows.forEach((r, idx) => {
    if (y > 545) {
      pdf.addPage();
      y = 40;
      drawHeader();
    }

    const values = [
      idx + 1,
      r.applicationId || r.id || "—",
      r.pacsName || "—",
      r.state || "—",
      r.district || "—",
      r.taluka || "—",
      `${r.capacityMt || "—"} MT`,
      r.siteVisitStatus || "NEW",
      r.siteVisitAssignedTo || "—",
    ];

    let x = 28;

    values.forEach((v, i) => {
      pdf.rect(x, y, widths[i], 24);
      pdf.text(String(v).slice(0, i === 2 ? 28 : 18), x + 4, y + 15);
      x += widths[i];
    });

    y += 24;
  });

  pdf.save("NAFED_Field_Visit_Report.pdf");
}

export default function PmcFieldVisitPane({ nafedScope }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [stateFilter, setStateFilter] = useState("all");
  const [districtFilter, setDistrictFilter] = useState("all");
  const [q, setQ] = useState("");
  const [uploadingId, setUploadingId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);

      const snap = await getDocs(collection(db, "godown_projects"));

      const list = snap.docs
        .map((d) => normalizeRow({ id: d.id, ...d.data() }))
        .filter((r) => applyNafedScopeFilter([r], nafedScope).length > 0)
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

      setRows(list);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load NAFED field visit projects.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [JSON.stringify(nafedScope || {})]);

  const states = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => r.state && set.add(r.state));
    return ["all", ...Array.from(set).sort()];
  }, [rows]);

  const districts = useMemo(() => {
    const set = new Set();
    rows
      .filter((r) => stateFilter === "all" || r.state === stateFilter)
      .forEach((r) => r.district && set.add(r.district));
    return ["all", ...Array.from(set).sort()];
  }, [rows, stateFilter]);

  const filtered = useMemo(() => {
    let list = [...rows];

    if (stateFilter !== "all") {
      list = list.filter((r) => r.state === stateFilter);
    }

    if (districtFilter !== "all") {
      list = list.filter((r) => r.district === districtFilter);
    }

    if (statusFilter !== "all") {
      list = list.filter((r) => (r.siteVisitStatus || "NEW") === statusFilter);
    }

    const needle = q.trim().toLowerCase();

    if (needle) {
      list = list.filter((r) => {
        const hay = [
          r.applicationId,
          r.pacsName,
          r.pacsRegistrationNo,
          r.village,
          r.taluka,
          r.district,
          r.state,
          r.contactPhone,
          r.id,
        ]
          .map((x) => String(x || "").toLowerCase())
          .join("|");

        return hay.includes(needle);
      });
    }

    return list;
  }, [rows, statusFilter, stateFilter, districtFilter, q]);

  const stats = useMemo(() => {
    const total = rows.length;
    const n = rows.filter((r) => (r.siteVisitStatus || "NEW") === "NEW").length;
    const p = rows.filter((r) => r.siteVisitStatus === "PLANNED").length;
    const d = rows.filter((r) => r.siteVisitStatus === "DONE").length;
    return { total, newCount: n, planned: p, done: d };
  }, [rows]);

  const planVisit = async (proj) => {
    const when =
      window.prompt(
        "Visit date & time (e.g. 05/12/2026 11:00 AM):",
        proj.siteVisitPlanDate || ""
      ) || "";

    if (!when) return;

    const engineer =
      window.prompt("Visit engineer / team name:", proj.siteVisitAssignedTo || "") || "";

    if (!engineer) return;

    try {
      await updateDoc(doc(db, "godown_projects", proj.id), {
        siteVisitStatus: "PLANNED",
        siteVisitPlanDate: when,
        siteVisitAssignedTo: engineer,
        workflowUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast.success("Visit planned.");

      setRows((prev) =>
        prev.map((r) =>
          r.id === proj.id
            ? normalizeRow({
                ...r,
                siteVisitStatus: "PLANNED",
                siteVisitPlanDate: when,
                siteVisitAssignedTo: engineer,
              })
            : r
        )
      );
    } catch (e) {
      console.error(e);
      toast.error("Failed to plan visit.");
    }
  };

  const markVisitDone = async (proj) => {
    const report =
      window.prompt(
        "Short visit report / observations:",
        proj.siteVisitReportText || ""
      ) || "";

    if (!report) return;

    try {
      await updateDoc(doc(db, "godown_projects", proj.id), {
        siteVisitStatus: "DONE",
        siteVisitReportText: report,
        workflowStage: "FIELD_VISIT_DONE",
        workflowUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast.success("Visit marked as DONE.");

      setRows((prev) =>
        prev.map((r) =>
          r.id === proj.id
            ? normalizeRow({
                ...r,
                siteVisitStatus: "DONE",
                siteVisitReportText: report,
                workflowStage: "FIELD_VISIT_DONE",
              })
            : r
        )
      );
    } catch (e) {
      console.error(e);
      toast.error("Failed to mark visit done.");
    }
  };

  const uploadVisitDocs = async (proj, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    try {
      setUploadingId(proj.id);

      const existing = Array.isArray(proj.siteVisitDocs)
        ? proj.siteVisitDocs
        : [];

      const uploaded = [];

      for (const file of files) {
        const path = `godown_visits/${proj.id}/${Date.now()}_${file.name}`;
        const ref = storageRef(storage, path);

        await uploadBytes(ref, file);

        const url = await getDownloadURL(ref);

        uploaded.push({
          name: file.name,
          url,
          path,
          uploadedAt: new Date().toISOString(),
        });
      }

      await updateDoc(doc(db, "godown_projects", proj.id), {
        siteVisitDocs: [...existing, ...uploaded],
        workflowUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      toast.success("Visit documents uploaded.");

      setRows((prev) =>
        prev.map((r) =>
          r.id === proj.id
            ? normalizeRow({
                ...r,
                siteVisitDocs: [...existing, ...uploaded],
              })
            : r
        )
      );
    } catch (e) {
      console.error(e);
      toast.error("Upload failed.");
    } finally {
      setUploadingId(null);
    }
  };

  const renderVisitBadge = (status) => {
    const s = status || "NEW";

    if (s === "DONE") return <Badge type="done">DONE</Badge>;
    if (s === "PLANNED") return <Badge type="planned">PLANNED</Badge>;
    return <Badge type="new">NEW</Badge>;
  };

  return (
    <div className="fv-page">
      <style>{`
        .fv-page {
          display: grid;
          gap: 16px;
          font-family: Inter, "Segoe UI", Arial, sans-serif;
        }

        .fv-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
        }

        .fv-stat {
          background: white;
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          padding: 16px;
          box-shadow: 0 8px 22px rgba(15,23,42,.05);
        }

        .fv-stat label {
          display: block;
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .12em;
        }

        .fv-stat b {
          display: block;
          margin-top: 8px;
          color: #071f45;
          font-size: 26px;
          font-weight: 950;
        }

        .fv-toolbar {
          background: white;
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          padding: 14px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 10px;
          box-shadow: 0 8px 22px rgba(15,23,42,.05);
        }

        .fv-toolbar label {
          display: block;
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .08em;
          margin-bottom: 5px;
        }

        .fv-input {
          width: 100%;
          height: 38px;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          padding: 0 10px;
          font-size: 12px;
          font-weight: 800;
          outline: none;
          background: white;
          color: #071f45;
        }

        .fv-actions {
          background: white;
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          padding: 12px 14px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          box-shadow: 0 8px 22px rgba(15,23,42,.05);
        }

        .fv-btn {
          border: 0;
          border-radius: 10px;
          padding: 9px 12px;
          font-size: 12px;
          font-weight: 950;
          cursor: pointer;
        }

        .fv-btn.primary {
          background: #062b55;
          color: white;
        }

        .fv-btn.green {
          background: #16a34a;
          color: white;
        }

        .fv-btn.gray {
          background: #e2e8f0;
          color: #071f45;
          border: 1px solid #cbd5e1;
        }

        .fv-table-card {
          background: white;
          border: 1.5px solid #111827;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 8px 22px rgba(15,23,42,.05);
        }

        .fv-table-head {
          padding: 14px 16px;
          border-bottom: 1.5px solid #111827;
          background: #f8fafc;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .fv-table-head h3 {
          margin: 0;
          color: #071f45;
          font-size: 17px;
          font-weight: 950;
        }

        .fv-table-head span {
          color: #64748b;
          font-size: 12px;
          font-weight: 900;
        }

        .fv-table-wrap {
          width: 100%;
          overflow: visible;
        }

        .fv-table {
          width: 100%;
          border-collapse: collapse;
          table-layout: fixed;
          font-size: 11px;
        }

        .fv-table th {
          background: #e2e8f0;
          color: #071f45;
          text-align: left;
          padding: 10px 8px;
          border: 1px solid #94a3b8;
          font-size: 9px;
          text-transform: uppercase;
          letter-spacing: .08em;
          font-weight: 950;
          white-space: normal;
          overflow-wrap: anywhere;
        }

        .fv-table td {
          padding: 10px 8px;
          border: 1px solid #94a3b8;
          vertical-align: top;
          background: white;
          color: #0f172a;
          font-weight: 750;
          line-height: 1.45;
          overflow-wrap: anywhere;
          word-break: break-word;
        }

        .fv-table tbody tr:nth-child(even) td {
          background: #f8fafc;
        }

        .fv-table tbody tr:hover td {
          background: #eef6ff;
        }

        .fv-title {
          color: #071f45;
          font-weight: 950;
        }

        .muted {
          color: #64748b;
          font-size: 11px;
          line-height: 1.6;
          font-weight: 750;
        }

        .fv-badge {
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 10px;
          font-weight: 950;
          display: inline-block;
          white-space: nowrap;
        }

        .fv-badge.new {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #f59e0b;
        }

        .fv-badge.planned {
          background: #dbeafe;
          color: #1e40af;
          border: 1px solid #93c5fd;
        }

        .fv-badge.done {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #16a34a;
        }

        .fv-docs {
          display: grid;
          gap: 5px;
          max-height: 72px;
          overflow-y: auto;
          overflow-x: hidden;
        }

        .fv-docs a {
          color: #064a9b;
          text-decoration: underline;
          font-weight: 800;
          font-size: 11px;
        }

        .fv-row-actions {
          display: grid;
          gap: 6px;
        }

        .fv-row-actions .fv-btn {
          width: 100%;
          padding: 8px 5px;
          font-size: 10px;
          border-radius: 7px;
        }

        .fv-report-text {
          max-height: 66px;
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 3px;
          white-space: pre-wrap;
        }

        .fv-upload {
          margin-top: 8px;
        }

        .fv-upload input {
          position: absolute;
          width: 1px;
          height: 1px;
          opacity: 0;
          pointer-events: none;
        }

        .fv-upload-label {
          display: inline-block;
          border: 1px solid #9eabb9;
          background: #f8fafc;
          color: #24364b;
          border-radius: 6px;
          padding: 6px 8px;
          font-size: 9px;
          font-weight: 900;
          cursor: pointer;
        }

        .fv-empty {
          padding: 40px;
          text-align: center;
          color: #64748b;
          font-size: 13px;
          font-weight: 900;
        }

        @media(max-width:1100px) {
          .fv-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .fv-toolbar {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media(max-width:700px) {
          .fv-stats,
          .fv-toolbar {
            grid-template-columns: 1fr;
          }

          .fv-actions {
            flex-direction: column;
            align-items: flex-start;
          }

          .fv-table-card {
            border: 0;
            overflow: visible;
            background: transparent;
            box-shadow: none;
          }

          .fv-table-head {
            border: 1px solid #cbd5e1;
            border-radius: 12px;
            margin-bottom: 10px;
          }

          .fv-table,
          .fv-table tbody,
          .fv-table tr,
          .fv-table td {
            display: block;
            width: 100%;
          }

          .fv-table colgroup,
          .fv-table thead {
            display: none;
          }

          .fv-table tbody tr {
            margin-bottom: 12px;
            border: 1px solid #b8c4d1;
            border-radius: 12px;
            overflow: hidden;
            background: white;
          }

          .fv-table td,
          .fv-table tbody tr:nth-child(even) td {
            position: relative;
            min-height: 38px;
            padding: 10px 10px 10px 112px;
            border: 0;
            border-bottom: 1px solid #e2e8f0;
            background: white;
          }

          .fv-table td::before {
            content: attr(data-label);
            position: absolute;
            left: 10px;
            top: 11px;
            width: 92px;
            color: #526171;
            font-size: 9px;
            font-weight: 950;
            text-transform: uppercase;
          }

          .fv-table td:last-child {
            border-bottom: 0;
          }
        }
      `}</style>

      <section className="fv-stats">
        <StatCard label="Assigned Applications" value={stats.total} />
        <StatCard label="New / Pending Visit" value={stats.newCount} />
        <StatCard label="Visit Planned" value={stats.planned} />
        <StatCard label="Visit Done" value={stats.done} />
      </section>

      <section className="fv-toolbar">
        <div>
          <label>State</label>
          <select
            className="fv-input"
            value={stateFilter}
            onChange={(e) => {
              setStateFilter(e.target.value);
              setDistrictFilter("all");
            }}
          >
            {states.map((s) => (
              <option key={s} value={s}>
                {s === "all" ? "All States" : s}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>District</label>
          <select
            className="fv-input"
            value={districtFilter}
            onChange={(e) => setDistrictFilter(e.target.value)}
          >
            {districts.map((d) => (
              <option key={d} value={d}>
                {d === "all" ? "All Districts" : d}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Visit Status</label>
          <select
            className="fv-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="NEW">New</option>
            <option value="PLANNED">Planned</option>
            <option value="DONE">Done</option>
          </select>
        </div>

        <div>
          <label>Search</label>
          <input
            className="fv-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Application / PACS / village / mobile..."
          />
        </div>
      </section>

      <section className="fv-actions">
        <div className="muted">
          Showing <b>{filtered.length}</b> records from PMC allocated districts only.
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="fv-btn gray" onClick={load}>
            Refresh
          </button>

          <button className="fv-btn green" onClick={() => exportCsv(filtered)}>
            Excel / CSV
          </button>

          <button className="fv-btn primary" onClick={() => exportPdf(filtered)}>
            PDF Report
          </button>
        </div>
      </section>

      <section className="fv-table-card">
        <div className="fv-table-head">
          <h3>NAFED Field Visit Applications</h3>
          <span>{filtered.length} Records</span>
        </div>

        {loading ? (
          <div className="fv-empty">Loading field visit applications...</div>
        ) : filtered.length === 0 ? (
          <div className="fv-empty">
            No field visit application found for this PMC allocation.
          </div>
        ) : (
          <div className="fv-table-wrap">
            <table className="fv-table">
              <colgroup>
                <col style={{ width: "4%" }} />
                <col style={{ width: "21%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "22%" }} />
                <col style={{ width: "11%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Sr</th>
                  <th>Application / PACS</th>
                  <th>Project Location</th>
                  <th>Capacity</th>
                  <th>Status & Visit Plan</th>
                  <th>Report & Documents</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((r, idx) => {
                  const visitDocs = Array.isArray(r.siteVisitDocs)
                    ? r.siteVisitDocs
                    : [];

                  return (
                    <tr key={r.id}>
                      <td data-label="Sr">{idx + 1}</td>

                      <td data-label="Application">
                        <div className="fv-title">
                          {r.applicationId || r.id}
                        </div>
                        <div className="muted">PACS: {r.pacsName || "—"}</div>
                        <div className="muted">
                          Reg: {r.pacsRegistrationNo || "—"}
                        </div>
                        <div className="muted">
                          Contact: {r.contactName || "—"} |{" "}
                          {r.contactPhone || "—"}
                        </div>
                      </td>

                      <td data-label="Location">
                        <div className="fv-title">{r.state || "—"}</div>
                        <div className="fv-title">{r.district || "—"}</div>
                        <div className="muted">
                          {[r.taluka, r.village].filter(Boolean).join(" / ") || "—"}
                        </div>
                      </td>

                      <td data-label="Capacity">
                        <div className="fv-title">
                          {r.capacityMt || "—"} MT
                        </div>
                        <div className="muted">{r.godownType || ""}</div>
                      </td>

                      <td data-label="Visit Plan">
                        <div style={{ marginBottom: 7 }}>
                          {renderVisitBadge(r.siteVisitStatus)}
                        </div>
                        <div className="muted">
                          <b>Assigned:</b> {r.siteVisitAssignedTo || "—"}
                        </div>
                        <div className="muted">
                          <b>Date:</b> {r.siteVisitPlanDate || "—"}
                        </div>
                      </td>

                      <td data-label="Report / Files">
                        {r.siteVisitReportText ? (
                          <div className="fv-report-text">
                            {r.siteVisitReportText}
                          </div>
                        ) : (
                          <span className="muted">No report yet.</span>
                        )}
                        <div style={{ borderTop: "1px solid #e2e8f0", marginTop: 8, paddingTop: 7 }}>
                        {visitDocs.length ? (
                          <div className="fv-docs">
                            {visitDocs.map((f, i) => (
                              <a
                                key={i}
                                href={f.url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {i + 1}. {f.name || "Visit Document"}
                              </a>
                            ))}
                          </div>
                        ) : (
                          <span className="muted">No documents.</span>
                        )}

                        <div className="fv-upload">
                          <label className="fv-upload-label" htmlFor={`visit-files-${r.id}`}>
                            {uploadingId === r.id ? "Uploading..." : "+ Upload Files"}
                          </label>
                          <input
                            id={`visit-files-${r.id}`}
                            type="file"
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png"
                            disabled={uploadingId === r.id}
                            onChange={(e) => {
                              const fl = e.target.files;
                              if (fl && fl.length) {
                                uploadVisitDocs(r, fl);
                                e.target.value = "";
                              }
                            }}
                          />
                          {uploadingId === r.id && (
                            <div className="muted">Uploading...</div>
                          )}
                        </div>
                        </div>
                      </td>

                      <td data-label="Actions">
                        <div className="fv-row-actions">
                          <button
                            type="button"
                            onClick={() => planVisit(r)}
                            className="fv-btn primary"
                          >
                            Plan Visit
                          </button>

                          <button
                            type="button"
                            onClick={() => markVisitDone(r)}
                            className="fv-btn green"
                          >
                            Mark Done
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
